import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";

// Per-page preferences ("shift+click to set as default"). Account-synced when
// signed in (stored on account.page_prefs), with a localStorage mirror for
// instant reads + logged-out use. Categories:
//   landing      : { "<league>": "/route" }        — first page on open
//   team_default : { "<league>": "TEAMID" }         — league-wide default team
//   page_team    : { "<route>": "TEAMID" }           — per-page team override
//   page_filter  : { "<route>": "value" }            — e.g. schedule scope
//   page_toggle  : { "<route>": "value" }            — e.g. preseason/regular
const LS = "rosterly_page_prefs";
const readLS = () => { try { return JSON.parse(localStorage.getItem(LS) || "{}") || {}; } catch { return {}; } };
const writeLS = (p) => { try { localStorage.setItem(LS, JSON.stringify(p)); } catch { /* noop */ } };

// `page_toggle` = access-zone toggles (Compact, Spoilers, Predict, Extensions,
// Builder…). Kept per DEVICE TYPE (desktop vs phone/tablet) but synced on the
// account as page_toggle_desktop / page_toggle_mobile — so the installed desktop
// app and the desktop browser share them, while mobile keeps its own.
const DEVICE_ONLY = "page_toggle";
const devKey = () => `page_toggle_${typeof window !== "undefined" && window.innerWidth >= 1024 ? "desktop" : "mobile"}`;
const stripDeviceOnly = (p) => { const { [DEVICE_ONLY]: mine, ...rest } = p; return { ...rest, [devKey()]: mine || {} }; };

const PagePrefsContext = createContext({ prefs: {}, setPref: () => {}, clearPref: () => {}, getPref: () => null });

// Schedule "Predict" ⇄ Standings predict toggles move together (PWHL and NHL).
const LINKED_TOGGLES = { sched_predict: "/standings:predict", "/standings:predict": "sched_predict", nhl_sched_predict: "nhl_standings_predicted", nhl_standings_predicted: "nhl_sched_predict" };

export function PagePrefsProvider({ children }) {
    const [prefs, setPrefs] = useState(readLS);
    const { data: account } = useQuery({
        queryKey: ["account"], queryFn: () => api.get("/account").then((r) => r.data),
        retry: false, staleTime: 60 * 1000,
    });
    const loggedIn = !!account?.profile?.id;

    // Account is the source of truth when signed in — hydrate the local mirror,
    // but PRESERVE this device's `page_toggle` (access-zone) settings.
    useEffect(() => {
        const ap = account?.profile?.page_prefs;
        if (ap && typeof ap === "object") {
            setPrefs((prev) => {
                const synced = ap[devKey()];
                const merged = { ...ap, [DEVICE_ONLY]: synced && typeof synced === "object" ? synced : (prev[DEVICE_ONLY] || {}) };
                writeLS(merged);
                return merged;
            });
        }
    }, [account]);

    const persist = useCallback((next) => {
        setPrefs(next); writeLS(next);
        if (loggedIn) api.put("/account", { page_prefs: stripDeviceOnly(next) }).catch(() => {});
    }, [loggedIn]);

    const setPref = useCallback((category, key, value) => {
        setPrefs((prev) => {
            const cat = { ...(prev[category] || {}) };
            const keys = category === "page_toggle" && LINKED_TOGGLES[key] ? [key, LINKED_TOGGLES[key]] : [key];
            keys.forEach((k) => { if (value == null || value === "") delete cat[k]; else cat[k] = value; });
            const next = { ...prev, [category]: cat };
            writeLS(next);
            if (loggedIn) api.put("/account", { page_prefs: stripDeviceOnly(next) }).catch(() => {});
            return next;
        });
    }, [loggedIn]);

    const clearPref = useCallback((category, key) => setPref(category, key, null), [setPref]);
    const getPref = useCallback((category, key) => (prefs[category] || {})[key] ?? null, [prefs]);

    return (
        <PagePrefsContext.Provider value={{ prefs, setPref, clearPref, getPref, persist }}>
            {children}
        </PagePrefsContext.Provider>
    );
}

export const usePagePrefs = () => useContext(PagePrefsContext);
